import { useIntl } from 'react-intl';
import { useApiClient } from '../../apiClient/useApiClient';
import { useShowError } from '../../useShowError';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useMemo } from 'react';
import { RealmSession } from '../../apiClient/types';

export function useLoadGroups() {
  const queryClient = useQueryClient();
  const { getSessionInformation, updateSessionGroups } = useApiClient();
  const showError = useShowError();
  const intl = useIntl();

  const {
    data: sessionData,
    isLoading,
    refetch,
  } = useQuery(['sessionInformation'], getSessionInformation, {
    onError: (error: unknown) => {
      showError(error, {
        description: intl.formatMessage({ id: 'session_groups_load_groups_error' }),
      });
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const realms = useMemo(() => {
    return sessionData ? sessionData.realms : [];
  }, [sessionData]);

  const groups = useMemo(() => {
    return realms ? realms.flatMap((realm: RealmSession) => realm.groups) : [];
  }, [realms]);

  const mutation = useMutation((activeGroupIds: string[]) => updateSessionGroups(activeGroupIds), {
    onSuccess: () => {
      queryClient.invalidateQueries('sessionGroups');
      queryClient.invalidateQueries('realms');
      queryClient.invalidateQueries('realm');
    },
    onError: (error: Error) => {
      showError(error, {
        description: intl.formatMessage({ id: 'session_load_groups_error ' }),
      });
    },
  });

  const setSessionGroups = async (activeGroupIds: string[]) => {
    await mutation.mutateAsync(activeGroupIds);
  };

  return { isLoading, realms, groups, setSessionGroups, refetch };
}
